import { useState, useEffect, useRef } from 'react';
import {
  startSignalRConnection,
  onSignalREvent,
  stopSignalRConnection,
  onConnectionStatusChange,
  SignalRConnectionStatus,
} from '../services/signalRService';

interface UseSignalRConnectionOptions {
  /** Whether to connect (tie to auth state). */
  enabled?: boolean;
}

interface UseSignalRConnectionResult {
  isConnected: boolean;
  connectionState: SignalRConnectionStatus;
  error: string | null;
}

/**
 * Hook to manage the global SignalR connection lifecycle.
 * Call once near the top of the component tree (e.g. AuthenticatedApp).
 */
export function useSignalRConnection(
  options: UseSignalRConnectionOptions = {},
): UseSignalRConnectionResult {
  const { enabled = true } = options;
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionState, setConnectionState] = useState<SignalRConnectionStatus>('Disconnected');

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const connect = async () => {
      try {
        setError(null);
        await startSignalRConnection();
        if (!cancelled) {
          setIsConnected(true);
          setConnectionState('Connected');
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'SignalR connection failed';
          setError(message);
          setIsConnected(false);
          setConnectionState('Disconnected');
          console.error('[SignalR] Connection error:', err);
        }
      }
    };

    connect();

    // Subscribe to ongoing status changes (reconnecting, reconnected, closed)
    const unsubscribe = onConnectionStatusChange((status) => {
      if (cancelled) return;
      setConnectionState(status);
      setIsConnected(status === 'Connected');
      if (status === 'Disconnected') {
        setError('Connection lost');
      } else {
        setError(null);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
      stopSignalRConnection();
      setIsConnected(false);
      setConnectionState('Disconnected');
    };
  }, [enabled]);

  return { isConnected, connectionState, error };
}

/**
 * Hook to subscribe to a specific SignalR event.
 * The callback fires whenever the server broadcasts this event.
 */
export function useSignalREvent(eventName: string, handler: (data: unknown) => void): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const stableHandler = (data: unknown) => handlerRef.current(data);
    const unsubscribe = onSignalREvent(eventName, stableHandler);
    return unsubscribe;
  }, [eventName]);
}
